import type { MessageConfig } from './message-config';

import { NetworkStatus } from '@apollo/client';
import { useMutation, useQuery, useSubscription } from '@apollo/client/react';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Text,
  VStack,
  Wrap,
} from '@chakra-ui/react';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { FiPaperclip, FiX } from 'react-icons/fi';

import { MessageList } from '#components/message-list/message-list';
import { MessageMarkdownComposerRow } from '#components/message-markdown-composer-row/message-markdown-composer-row';
import { toaster } from '#components/toaster/toaster';
import { MESSAGE_ATTACHMENT_UPLOAD_IDS } from '#constants/message-context-names';
import { buildGetMessagesVariablesForNpMessageTarget } from '#graphql/build-get-messages-variables';
import { mergeCreateMessageIntoGetMessagesCache } from '#graphql/cache/merge-create-message-into-get-messages';
import { CREATE_MESSAGE } from '#graphql/mutations/create-message';
import { DELETE_UPLOAD } from '#graphql/mutations/delete-upload';
import { GET_MESSAGES } from '#graphql/queries/messages';
import { INBOX_MESSAGES_UPDATED } from '#graphql/subscriptions/inbox-messages-updated';
import { useScrollMessageListToEnd } from '#hooks/use-scroll-message-list-to-end';
import type { PendingMessageAttachment } from '#pages/document/document-chat-composer-context';
import {
  composerAttachmentMarkdownLine,
  uploadFileForMessageDraft,
} from '#pages/document/upload-image';
import { useAppConfig } from '#providers/app-config-provider';
import { useCurrentUser, useUser } from '#providers/user-provider';
import {
  resolveFocusParamToRootId,
  type MessagesQueryRoot,
} from '#utils/message-thread-focus';

import {
  describeMessage,
  getMessageValidationMessage,
  normalizeMessageConfig,
  type MessageBlockTarget,
} from './message-config';

export type { MessagesQueryRoot };

export function computeMessagePinnedRootId(
  target: MessageBlockTarget,
  messageId: string | undefined,
  sortedMessages: MessagesQueryRoot[],
): string | null {
  if (target !== 'message') {
    return null;
  }

  const trimmed = messageId?.trim();
  if (!trimmed) {
    return null;
  }

  return resolveFocusParamToRootId(trimmed, sortedMessages);
}

export function computeMessageDisplayMessages(
  sortedMessages: MessagesQueryRoot[],
  target: MessageBlockTarget,
  focusedRootId: string | null,
  pinnedRootId: string | null,
): MessagesQueryRoot[] {
  if (target === 'message') {
    if (!pinnedRootId) {
      return [];
    }

    return sortedMessages.filter((message) => message.id === pinnedRootId);
  }

  if (focusedRootId) {
    return sortedMessages.filter((message) => message.id === focusedRootId);
  }

  return sortedMessages;
}

export interface UseMessageBlockPanelOptions {
  config: MessageConfig;
  documentId?: string;
  isNewDocument: boolean;
  getSerializedDocument: () => string;
}

export function useMessageBlockPanel({
  config,
  documentId,
  isNewDocument,
  getSerializedDocument,
}: UseMessageBlockPanelOptions) {
  const normalizedConfig = normalizeMessageConfig(config);
  const { target } = normalizedConfig;
  const { config: appConfig } = useAppConfig();
  const apiHost = appConfig.apiHost;
  const user = useCurrentUser();
  const { user: authUser } = useUser();

  const getMessagesVariables = useMemo(
    () => buildGetMessagesVariablesForNpMessageTarget(target, documentId ?? ''),
    [documentId, target],
  );

  const skipQuery = target === 'document' && !documentId;

  const {
    data: messagesData,
    error: rawMessagesError,
    networkStatus: messagesNetworkStatus,
    refetch: refetchMessages,
  } = useQuery(GET_MESSAGES, {
    variables: getMessagesVariables,
    fetchPolicy: 'cache-and-network',
    skip: skipQuery,
  });

  useSubscription(INBOX_MESSAGES_UPDATED, {
    skip: skipQuery || !authUser?.email?.trim(),
    onData: () => {
      void refetchMessages();
    },
  });

  const [localFocusedRootId, setLocalFocusedRootId] = useState<string | null>(
    null,
  );
  const [messageText, setMessageText] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingMessageAttachment[]
  >([]);
  const [attachmentUploadBusy, setAttachmentUploadBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [createMessage, { loading: createMessageLoading }] =
    useMutation(CREATE_MESSAGE);
  const [deleteUpload] = useMutation(DELETE_UPLOAD);

  const messagesError = skipQuery ? undefined : rawMessagesError;

  const isMessagesInitialLoad =
    messagesNetworkStatus === NetworkStatus.loading && messagesData == null;

  const sortedMessages = useMemo(
    () =>
      [...(messagesData?.messages || [])].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [messagesData?.messages],
  );

  const pinnedRootId = useMemo(
    () =>
      computeMessagePinnedRootId(
        target,
        normalizedConfig.messageId,
        sortedMessages,
      ),
    [normalizedConfig.messageId, sortedMessages, target],
  );

  const focusedRootId =
    target === 'message' ? pinnedRootId : localFocusedRootId;

  const displayMessages = useMemo(
    () =>
      computeMessageDisplayMessages(
        sortedMessages,
        target,
        focusedRootId,
        pinnedRootId,
      ),
    [focusedRootId, pinnedRootId, sortedMessages, target],
  );

  const expandThread = useCallback(
    (rootId: string) => {
      if (target === 'message') {
        return;
      }

      setLocalFocusedRootId(rootId);
    },
    [target],
  );

  const collapseThread = useCallback(() => {
    if (target === 'message') {
      return;
    }

    setLocalFocusedRootId(null);
  }, [target]);

  const hasSendableContent =
    messageText.trim().length > 0 || pendingAttachments.length > 0;

  const composerDisabledForDocument =
    target === 'document' && (isNewDocument || !documentId);

  const composerDisabledForPinnedMessage =
    target === 'message' && !pinnedRootId && !isMessagesInitialLoad;

  const composerDisabled =
    composerDisabledForDocument ||
    composerDisabledForPinnedMessage ||
    createMessageLoading ||
    attachmentUploadBusy;

  const handleSendMessage = async () => {
    if (!hasSendableContent || composerDisabled) {
      return;
    }

    if (target === 'message' && !pinnedRootId) {
      return;
    }

    const trimmed = messageText.trim();
    const bodyParts: string[] = [];
    if (trimmed.length > 0) {
      bodyParts.push(trimmed);
    }

    for (const att of pendingAttachments) {
      bodyParts.push(
        composerAttachmentMarkdownLine(
          att.filename,
          att.id,
          att.url,
          att.isEncrypted,
          apiHost,
        ),
      );
    }
    const value = bodyParts.join('\n\n');
    const hadNoThreadFocus = !focusedRootId;

    try {
      const contexts: { name: string; value: string }[] = [];

      if (target === 'document' && documentId && !focusedRootId) {
        contexts.push({ name: 'documentId', value: documentId });
      }

      if (target === 'document' && !focusedRootId) {
        const snap = getSerializedDocument();
        if (snap.length > 0) {
          contexts.push({
            name: 'currentDocumentValue',
            value: snap,
          });
        }
      }

      if (pendingAttachments.length > 0) {
        contexts.push({
          name: MESSAGE_ATTACHMENT_UPLOAD_IDS,
          value: JSON.stringify(pendingAttachments.map((a) => a.id)),
        });
      }

      const parentMessageId =
        target === 'message'
          ? (pinnedRootId ?? undefined)
          : (focusedRootId ?? undefined);

      const { data } = await createMessage({
        variables: {
          input: {
            value,
            parentMessageId,
            ...(contexts.length > 0 ? { contexts } : {}),
          },
        },
        update(cache, { data: mutationData }) {
          if (mutationData?.createMessage) {
            mergeCreateMessageIntoGetMessagesCache(
              cache,
              mutationData.createMessage,
              getMessagesVariables,
            );
          }
        },
      });

      setMessageText('');
      setPendingAttachments([]);

      if (target === 'document') {
        await refetchMessages();
      }

      const createdId = data?.createMessage?.id;
      if (hadNoThreadFocus && createdId && target !== 'message') {
        expandThread(createdId);
      }
    } catch (error) {
      toaster.create({
        title: 'Failed to send message',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    }
  };

  const onAttachmentFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) {
        return;
      }

      try {
        setAttachmentUploadBusy(true);
        const result = await uploadFileForMessageDraft(file, { apiHost });
        setPendingAttachments((prev) => [
          ...prev,
          {
            id: result.id,
            filename: file.name,
            url: result.url,
            isEncrypted: result.isEncrypted,
          },
        ]);
      } catch (error) {
        toaster.create({
          title: 'Upload failed',
          description: error instanceof Error ? error.message : 'Unknown error',
          type: 'error',
        });
      } finally {
        setAttachmentUploadBusy(false);
      }
    },
    [apiHost],
  );

  const removeDraftAttachment = useCallback(
    async (id: string) => {
      try {
        await deleteUpload({ variables: { id } });
      } catch (error) {
        toaster.create({
          title: 'Could not remove file',
          description: error instanceof Error ? error.message : 'Unknown error',
          type: 'error',
        });
        return;
      }
      setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
    },
    [deleteUpload],
  );

  const placeholder = composerDisabledForDocument
    ? 'Save the document first to use document messages'
    : composerDisabledForPinnedMessage
      ? 'Message thread not found'
      : focusedRootId
        ? 'Type a reply…'
        : 'Type a message…';

  const emptyMessage =
    target === 'message' && !pinnedRootId && !isMessagesInitialLoad
      ? normalizedConfig.messageId?.trim()
        ? `No thread found for message ID “${normalizedConfig.messageId.trim()}”.`
        : 'Set a message ID to pin a thread.'
      : target === 'document'
        ? 'No messages for this document yet.'
        : 'No messages found.';

  return {
    userEmail: user.email,
    target,
    caption: describeMessage(normalizedConfig),
    configValidationMessage: getMessageValidationMessage(normalizedConfig),
    displayMessages,
    focusedRootId,
    isMessagesInitialLoad,
    messagesError,
    refetchMessages,
    expandThread,
    collapseThread,
    messageText,
    setMessageText,
    pendingAttachments,
    fileInputRef,
    onAttachmentFileChange,
    removeDraftAttachment,
    handleSendMessage,
    hasSendableContent,
    composerDisabled,
    createMessageLoading,
    attachmentUploadBusy,
    placeholder,
    emptyMessage,
    canCollapseThread: target !== 'message' && Boolean(focusedRootId),
  };
}

export type MessageBlockPanelProps = UseMessageBlockPanelOptions;

export function MessageBlockPanel(hookOptions: MessageBlockPanelProps) {
  const panel = useMessageBlockPanel(hookOptions);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useScrollMessageListToEnd(messagesEndRef, panel.displayMessages);

  if (!panel.userEmail) {
    return (
      <Text fontSize="sm" color="fg.muted">
        Sign in to view messages.
      </Text>
    );
  }

  return (
    <VStack
      align="stretch"
      gap={2}
      flex={1}
      minH={0}
      height="100%"
      className="np-message-block-panel"
    >
      <HStack justify="space-between" align="start" gap={2}>
        <Text fontSize="xs" color="fg.muted">
          {panel.caption}
        </Text>
      </HStack>

      {panel.configValidationMessage ? (
        <Text fontSize="sm" color="orange.600">
          {panel.configValidationMessage}
        </Text>
      ) : null}

      <Box
        flex={1}
        minH={0}
        overflow="hidden"
        display="flex"
        flexDirection="column"
        width="100%"
      >
        <MessageList
          messages={panel.displayMessages}
          loading={panel.isMessagesInitialLoad}
          error={panel.messagesError ?? undefined}
          emptyMessage={panel.emptyMessage}
          focusedRootId={panel.focusedRootId}
          onExpandThread={panel.expandThread}
          messagesEndRef={messagesEndRef}
          alignMessagesToBottom
          onMessageDeleted={() => void panel.refetchMessages()}
        />
      </Box>

      {panel.canCollapseThread ? (
        <HStack gap={2} align="center" fontSize="xs" color="fg.muted">
          <Text>Replying in thread</Text>
          <Button
            size="xs"
            variant="ghost"
            onClick={() => panel.collapseThread()}
          >
            Cancel
          </Button>
        </HStack>
      ) : null}

      {panel.pendingAttachments.length > 0 ? (
        <Wrap gap={1}>
          {panel.pendingAttachments.map((att) => (
            <HStack
              key={att.id}
              gap={1}
              px={2}
              py={1}
              borderRadius="md"
              bg="bg.subtle"
              fontSize="xs"
              maxW="100%"
            >
              <Text truncate title={att.filename}>
                {att.filename}
              </Text>
              <IconButton
                aria-label={`Remove ${att.filename}`}
                size="xs"
                variant="ghost"
                onClick={() => void panel.removeDraftAttachment(att.id)}
              >
                <FiX />
              </IconButton>
            </HStack>
          ))}
        </Wrap>
      ) : null}

      <input
        ref={panel.fileInputRef}
        type="file"
        hidden
        onChange={(ev) => void panel.onAttachmentFileChange(ev)}
      />

      <MessageMarkdownComposerRow
        value={panel.messageText}
        onChange={panel.setMessageText}
        onSubmit={() => void panel.handleSendMessage()}
        placeholder={panel.placeholder}
        loading={panel.createMessageLoading}
        disabled={panel.composerDisabled}
        submitDisabled={!panel.hasSendableContent || panel.composerDisabled}
        trailingActions={
          <IconButton
            type="button"
            aria-label="Attach file"
            title="Attach file"
            size="sm"
            variant="outline"
            loading={panel.attachmentUploadBusy}
            disabled={panel.composerDisabled}
            onClick={() => panel.fileInputRef.current?.click()}
          >
            <FiPaperclip />
          </IconButton>
        }
      />
    </VStack>
  );
}
